import { Component, Element, Event, EventEmitter, Fragment, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import {
  StzhAvailableTag,
  StzhChipselectChangeEvent,
  StzhChipselectItem,
  StzhCombinedGeoRefData,
  StzhGeoRefDataOverlayCloseClickEvent,
  StzhMapGeoRefData,
  StzhPoiGeoRefData
} from "../../../index";
import { Document } from "flexsearch/dist/flexsearch.bundle.min.js";
import { addMediaChangeListener, media, removeMediaChangeListener } from "../../../utils/media-utils";

const CLASS_BODY_OPEN = "stzh-overlay-open";

/**
 * @slot logo - Slot for brand logo
 */
@Component({
  tag: "stzh-geo-ref-data",
  styleUrl: "stzh-geo-ref-data.scss",
  shadow: true
})
export class GeoRefData {
  /** ID for this instance, can be used to open via URL query param "al=<id>" - name "al" for historical reasons */
  @Prop({ reflect: true }) al: string = "";

  /** Heading for the overlay. */
  @Prop({ reflect: true }) heading: string = "";

  /** The URL for fetching the card data */
  @Prop({ reflect: true }) mapDataUrl: string = "";

  /** The URL used for fetching the POI data. */
  @Prop({ reflect: true }) poiDataUrl: string = "";

  /** Base URL used for all external requests (map, card). */
  @Prop() baseUrlOverride: string = "https://www.stadt-zuerich.ch";

  /** Whether map should have a location control */
  @Prop({ reflect: true }) locationControl: boolean = true;

  /** Whether to display an icon marker in the center */
  @Prop() centerMarker: boolean = false;

  /** Initial zoom */
  @Prop() zoom: number = 7;

  /** Zoom set when location is enabled */
  @Prop() zoomLocation: number = 7;

  /** Minimal angle in radians to start a rotation */
  @Prop() pinchRotateThreshold: number = 0.3;

  /** To make sure all tags are mapped to their identificator, only these will be available. */
  @Prop() availableTags: StzhAvailableTag[] | string = [];
  private _availableTags: StzhAvailableTag[] = [];

  /** Reset tag (the first one). */
  @Prop() resetTag: StzhChipselectItem | string;
  private _resetTag: StzhChipselectItem;

  /** Fields for building the search index. */
  @Prop()
  searchIndexFields: string[] = [
    "title",
    "poiname",
    "street",
    "zip",
    "description"
  ];

  /** Fields for building the search index. */
  @Prop() searchIndexIdField: string = "poiid";

  @Watch("availableTags")
  availableTagsWatcher(newValue: StzhAvailableTag[] | string) {
    if (!newValue) {
      this._availableTags = [];
    } else if (typeof newValue === "string") {
      try {
        this._availableTags = JSON.parse(newValue);
      } catch (e) {
        this._availableTags = [];
      }
    } else {
      this._availableTags = newValue;
    }
  }

  @Watch("resetTag")
  resetTagWatcher(newValue: StzhChipselectItem | string) {
    if (!newValue) {
      this._resetTag = undefined;
    } else if (typeof newValue === "string") {
      try {
        this._resetTag = JSON.parse(newValue);
      } catch (e) {
        this._resetTag = undefined;
      }
    } else {
      this._resetTag = newValue;
    }
  }

  @State() filterActive: boolean = false;
  @State() searchActive: boolean = false;
  @State() showOnlyResultsForCurrentExtent: boolean = true;
  @State() finalResults: StzhCombinedGeoRefData[] = [];
  @State() finalResultsPaginatedList: StzhCombinedGeoRefData[] = [];
  @State() finalResultsPaginationOffset = 0;

  @State() viewmode: "map" | "list" = "map";
  @State() isViewportLarge: boolean;
  @State() isViewportMedium: boolean;
  @State() isInitialized: boolean;
  @State() isOpen: boolean;
  @State() isLoading: boolean;
  @State() numberOfItemsToLoad: number = 20;
  @State() canLoadMore: boolean;
  @State() appliedFilters: string[] = [""];
  @State() isFiltersContentOpen: boolean = false;

  /** Overlay close click event */
  @Event()
  stzhOverlayCloseClick: EventEmitter<StzhGeoRefDataOverlayCloseClickEvent>;

  private combinedData: StzhCombinedGeoRefData[];
  private enhancedTags: StzhChipselectItem[];
  private searchIndex: Document;

  private filterResults: StzhCombinedGeoRefData[] = [];
  private searchResults: Set<string>;

  @Element() element: HTMLStzhGeoRefDataElement;
  private mapElement: HTMLStzhOlmapElement;
  private toastbarElement: HTMLStzhToastbarElement;
  private metaWrapperElement: HTMLDivElement;
  private resultsListElement: HTMLDivElement;
  private resizeObserver: ResizeObserver;

  private applyFiltersFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);

    // Find all filter parameters from the URL
    const filters = Array.from(urlParams.entries())
      .filter(([key, value]) => key.startsWith("filters[") && value === "true")
      .map(([key]) => key.slice(8, -1)); // Extract the filter key (removing 'filters[' and ']')

    // Apply filters if they exist
    if (filters.length) {
      this.appliedFilters = filters;
      // Update the UI to show selected filters
      this.updateUrlWithFilters(); // Keep the URL sync logic here
      this.handleFilterChange({
        detail: { value: filters }
      } as CustomEvent<StzhChipselectChangeEvent>);
    }
  }

  private handleFilterToggleClick = () => {
    this.isFiltersContentOpen = true;
  };

  private handleApplyFilterClick = () => {
    this.isFiltersContentOpen = false;
  };

  private handleOverlayCloseClick = () => {
    // Remove the class from the body
    document.body.classList.remove(CLASS_BODY_OPEN);
    this.isOpen = false;

    // Get the current URL's query parameters
    const urlParams = new URLSearchParams(window.location.search);

    // Remove all filters (query parameters that start with "filters[")
    Array.from(urlParams.keys()).forEach(key => {
      if (key.startsWith("filters[")) {
        urlParams.delete(key);
      }
    });

    // Remove all extent parameters (keys like "extent[0]", "extent[1]", etc.)
    urlParams.delete("extent[0]");
    urlParams.delete("extent[1]");
    urlParams.delete("extent[2]");
    urlParams.delete("extent[3]");

    // Remove the al parameter
    urlParams.delete("al");

    // Update the URL without reloading the page
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    history.replaceState(null, "", newUrl);

    // Clear filters.
    this.appliedFilters = [""];
  };

  private handleResize = async () => {
    if (this.metaWrapperElement) {
      if (this.metaWrapperElement.clientHeight) {
        this.mapElement.style.height = `calc(100% - ${this.metaWrapperElement.clientHeight}px)`;
        this.resultsListElement.style.height = `calc(100% - ${this.metaWrapperElement.clientHeight}px)`;
      } else {
        this.mapElement.style.height = `100%`;
        this.resultsListElement.style.height = `100%`;
      }
      const map = await this.mapElement.getMap();
      map.updateSize();
    }
  };

  private mediaChangeHandler = () => {
    this.isViewportLarge = media("large").matches;
    this.isViewportMedium = media("medium").matches;
  };

  private async loadMapData(): Promise<StzhMapGeoRefData[]> {
    if (!this.mapDataUrl) {
      return [];
    }
    try {
      const response = await fetch(this.mapDataUrl);
      const rawMapData = await response.text();
      const parsedData = new DOMParser().parseFromString(rawMapData, "text/xml");
      return Array.from(parsedData.querySelectorAll("featureMember")).map(item => {
        const pos = item.querySelector("pos")?.textContent;
        const poiid = item.querySelector("poi_id")?.textContent;
        const [lat, lon] = pos?.split(" ") || [];

        return { poiid, lat, lon } as StzhMapGeoRefData;
      });
    } catch (error) {
      this.toastbarElement
        .toast("Es ist ein Fehler beim Beziehen der Kartendaten aufgetreten.", {
          type: "error"
        })
        .catch();
    }
  }

  private async loadPoiData() {
    if (!this.poiDataUrl) {
      return [];
    }
    try {
      const response = await fetch(this.poiDataUrl, { credentials: "include" });
      const poiData = await response.json();
      return poiData.items;
    } catch (error) {
      this.toastbarElement
        .toast("Beim Laden der POI-Daten ist ein Fehler aufgetreten.", {
          type: "error"
        })
        .catch();
    }
  }

  private combineData(
    firstArray: StzhMapGeoRefData[] | StzhPoiGeoRefData[],
    secondArray: StzhMapGeoRefData[] | StzhPoiGeoRefData[]
  ): StzhCombinedGeoRefData[] {
    if (!firstArray.length || !secondArray.length) {
      return [];
    }
    // determine which array is smaller for performance
    let smallerArray: any[];
    let largerArray: any[];

    if (firstArray.length < secondArray.length) {
      smallerArray = firstArray;
      largerArray = secondArray;
    } else {
      smallerArray = secondArray;
      largerArray = firstArray;
    }
    // combine arrays based on poiid
    return smallerArray
      .map(item => {
        const matchingItem = largerArray.find(largerArrayItem => largerArrayItem.poiid === item.poiid);
        return { ...item, ...matchingItem };
      })
      .sort((a, b) => {
        try {
          return a.title.localeCompare(b.title); // Sorts alphabetically by the 'name' property
        } catch (noTitleException) {
          return 1;
        }
      });
  }

  private enhanceTags(availableTags: StzhAvailableTag[], combinedData: StzhCombinedGeoRefData[]): StzhChipselectItem[] {
    return availableTags
      .sort((a, b) => {
        try {
          a.label.localeCompare(b.label);
        } catch (noLabelException) {
          return 1;
        }
      })
      .map(tag => {
        const counter = combinedData
          .filter(item => {
            try {
              return item.tags.includes(tag.value);
            } catch (noTagsException) {
              return false;
            }
          })
          .length.toString();
        return { ...tag, counter, variant: "filter" };
      });
  }

  private buildSearchIndex(data: StzhCombinedGeoRefData[]) {
    if (!data.length) {
      return;
    }
    this.searchIndex = new Document({
      tokenize: "forward",
      matcher: {
        "[äà]": "a",
        "[öó]": "o",
        "[üûúù]": "u",
        "[éè]": "e"
      },
      document: {
        id: this.searchIndexIdField,
        index: this.searchIndexFields
      }
    });
    data.forEach(item => {
      this.searchIndex.add(item);
    });
  }

  @Listen("stzhPostRender")
  @Listen("stzhMovePanZoom")
  async mapMovedPannedOrZoomed() {
    await this.setVisibleMarkers();
  }

  private async setVisibleMarkers() {
    if (this.combinedData) {
      const visibleMarkers = await this.mapElement.getVisibleMarkers();
      if (this.showOnlyResultsForCurrentExtent) {
        this.finalResults = this.combinedData.filter(item =>
          visibleMarkers.find(visibleMarker => visibleMarker.getId() === item.poiid)
        );
      } else {
        this.finalResults = this.combinedData;
      }
      this.setEnhancedTags();
      await this.setPaginatedResults();
    }
  }

  private async setPaginatedResults() {
    this.finalResultsPaginatedList = this.finalResults.slice(0, this.numberOfItemsToLoad);
    this.canLoadMore = this.finalResults.length > this.numberOfItemsToLoad;
  }

  private async checkDirectOpenViaUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const idFromUrl = urlParams.get("al");

    if (idFromUrl && idFromUrl === this.al) {
      await this.initializeAndOpenMap();
    }
  }

  connectedCallback() {
    this.mediaChangeHandler();
    addMediaChangeListener(this.mediaChangeHandler);
    this.resizeObserver = new ResizeObserver(this.handleResize);
    this.resizeObserver.observe(this.element);
    this.availableTagsWatcher(this.availableTags);
    this.resetTagWatcher(this.resetTag);
    this.checkDirectOpenViaUrl();
  }

  disconnectedCallback() {
    removeMediaChangeListener(this.mediaChangeHandler);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private async initializeAndOpenMap() {
    document.body.classList.add(CLASS_BODY_OPEN);
    this.isLoading = true;
    this.isOpen = true;

    // Write the "al" prop of the component to the current URL as query param if it is not already there
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("al")) {
      urlParams.set("al", this.al);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      history.replaceState(null, "", newUrl);
    }

    try {
      this.combinedData = this.combineData(await this.loadMapData(), await this.loadPoiData());
      this.setEnhancedTags();
      this.applyFiltersFromUrl();
      this.buildSearchIndex(this.combinedData);
      this.finalResults = this.combinedData;
      await this.setVisibleMarkers();
      await this.combineFilterAndSearchResults();
      await this.handleResize();

      const urlParams = new URLSearchParams(window.location.search);
      const extent = [
        parseFloat(urlParams.get("extent[0]")),
        parseFloat(urlParams.get("extent[1]")),
        parseFloat(urlParams.get("extent[2]")),
        parseFloat(urlParams.get("extent[3]"))
      ];

      if (!extent.length || isNaN(extent[0]) || isNaN(extent[1]) || isNaN(extent[2]) || isNaN(extent[3])) {
        await this.mapElement.updateViewToFitMarkers();
      }

      await this.handleResize();
      this.isInitialized = true;
    } catch (mapException) {
      this.toastbarElement
        .toast("Beim Laden der Karte ist ein Fehler aufgetreten.", {
          type: "error"
        })
        .catch();
    }

    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  private updateUrlWithFilters() {
    const urlParams = new URLSearchParams(window.location.search);

    // Remove existing filters from URL
    Array.from(urlParams.keys()).forEach(key => {
      if (key.startsWith("filters[")) {
        urlParams.delete(key);
      }
    });

    // Add the selected filters from `appliedFilters` to the URL
    this.appliedFilters.forEach(filter => {
      urlParams.set(`filters[${filter}]`, "true");
    });

    // Update the URL without reloading the page
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    history.replaceState(null, "", newUrl);
  }

  private setEnhancedTags() {
    this.enhancedTags = this.enhanceTags(this._availableTags, this.finalResults);
    const enhancedResetTag = {
      ...this._resetTag,
      counter: this.finalResults.length.toString()
    };
    if (this._resetTag) {
      this.enhancedTags.unshift(enhancedResetTag);
    }
  }

  private handleFilterChange = async (e: CustomEvent<StzhChipselectChangeEvent>) => {
    let filtersArray = Array.isArray(e.detail.value) ? e.detail.value : [e.detail.value];
    if (filtersArray.length === 1 && filtersArray[0] === this._resetTag.value) {
      this.appliedFilters = [];
    } else {
      try {
        filtersArray = filtersArray.filter(filterValue => filterValue !== "");
      } catch (noEmptyFilter) {
        // nothing to do here
      }
      this.appliedFilters = filtersArray;
    }
    setTimeout(async () => {
      await this.handleFilterResults();
    });
  };

  private async handleFilterResults() {
    // Update the component state based on the selected filters
    if (this.appliedFilters.length !== 0) {
      this.filterResults = this.finalResults.filter(
        item => item.tags && item.tags.some(tag => this.appliedFilters.includes(tag))
      );
      this.filterActive = true;
    } else {
      this.filterActive = false;
      this.filterResults = this.finalResults;
    }

    // Update the URL with the applied filters
    this.updateUrlWithFilters();

    // Combine the results from filters and search
    await this.combineFilterAndSearchResults();
  }

  private handleExtentVisibilityChange = async e => {
    this.showOnlyResultsForCurrentExtent = e.detail.checked;
    await this.setVisibleMarkers();
  };

  private handleSearchChange = async (e: CustomEvent) => {
    if (e.detail.value !== "") {
      if (!this.searchIndex) {
        return;
      }
      const searchResults = this.searchIndex.search(e.detail.value);
      const flatSearchResults = searchResults.flatMap(field => field.result);
      this.searchResults = new Set(flatSearchResults);
      this.searchActive = true;
    } else {
      this.searchActive = false;
    }
    await this.combineFilterAndSearchResults();
  };

  private async combineFilterAndSearchResults() {
    if (this.filterActive && !this.searchActive) {
      this.finalResults = this.filterResults;
    } else if (!this.filterActive && this.searchActive) {
      this.finalResults = Array.from(this.searchResults).map(id => this.combinedData.find(item => item.poiid === id));
    } else if (this.filterActive && this.searchActive) {
      this.finalResults = this.filterResults.filter(item => this.searchResults.has(item.poiid));
    } else {
      this.finalResults = this.combinedData;
    }

    this.finalResultsPaginatedList = this.finalResults.slice(0, this.numberOfItemsToLoad);

    await this.replaceMapMarkers(this.finalResults);
  }

  private loadMore() {
    this.finalResultsPaginationOffset++;
    const sliced = this.finalResults.slice(
      this.finalResultsPaginationOffset * this.numberOfItemsToLoad,
      this.finalResultsPaginationOffset * this.numberOfItemsToLoad + this.numberOfItemsToLoad
    );
    if (!sliced || !sliced.length || sliced.length < this.numberOfItemsToLoad) {
      this.canLoadMore = false;
    }
    this.finalResultsPaginatedList = this.finalResultsPaginatedList.concat(sliced);
  }

  private async replaceMapMarkers(items: StzhCombinedGeoRefData[]) {
    await this.mapElement.removeAllIconMarkers();
    items
      .filter(item => !!item.lat && !!item.lon)
      .forEach(item => {
        this.mapElement
          .addIconMarker([
            parseFloat(item.lat),
            parseFloat(item.lon)
          ], {
            id: item.poiid,
            title: item.title,
            text: item.description,
            data: item.data,
            imageUri: `${this.baseUrlOverride}${item.image}`,
            href: `${this.baseUrlOverride}${item.path}`
          })
          .catch();
      });
    if (!this.showOnlyResultsForCurrentExtent) {
      this.mapElement.updateViewToFitMarkers().catch();
    }
  }

  private toggleViewMode = () => {
    if (this.viewmode === "map") {
      this.viewmode = "list";
    } else {
      this.viewmode = "map";
    }
  };

  private async handlePoiTeaserMouseEnter(item: StzhCombinedGeoRefData) {
    const marker = await this.mapElement.findMarkerById(item.poiid);
    if (marker) {
      await this.mapElement.setIconMarkerActive(marker);
    }
  }

  private async handlePoiTeaserMouseLeave(item: StzhCombinedGeoRefData) {
    const marker = await this.mapElement.findMarkerById(item.poiid);
    if (marker) {
      await this.mapElement.setIconMarkerInactive(marker);
    }
  }

  render() {
    const classes = {
      "stzh-geo-ref-data": true,
      "stzh-geo-ref-data--is-loading": this.isLoading,
      "stzh-geo-ref-data--is-open": this.isOpen,
      "stzh-geo-ref-data--has-filters": this._availableTags.length > 0
    };
    return (
      <Host>
        <stzh-toastbar ref={el => (this.toastbarElement = el as HTMLStzhToastbarElement)} />
        <div class="stzh-geo-ref-data__trigger" onClick={() => this.initializeAndOpenMap()}>
          <slot name="trigger"></slot>
        </div>
        <stzh-overlay open={this.isLoading}>
          <stzh-loader label="Daten werden geladen..."></stzh-loader>
        </stzh-overlay>
        <div class={classes}>
          <div class="stzh-geo-ref-data__header-wrapper">
            <stzh-header class="stzh-geo-ref-data__header" hide-metabar={true} sticky="disabled">
              <slot name="logo" slot="logo"></slot>
            </stzh-header>
            <div class="stzh-geo-ref-data__heading stzh-geo-ref-data--content-padding">
              <stzh-heading class="stzh-geo-ref-data__heading-heading" curve="h2">
                {this.heading}
              </stzh-heading>
              <stzh-button
                class="stzh-geo-ref-data__overlay-close"
                icon="close"
                iconOnly={true}
                size="small"
                variant="tertiary"
                onClick={this.handleOverlayCloseClick}
              ></stzh-button>
            </div>
          </div>

          <div class="stzh-geo-ref-data__content">
            <div
              class="stzh-geo-ref-data__meta-wrapper stzh-geo-ref-data--content-padding"
              ref={el => (this.metaWrapperElement = el as HTMLDivElement)}
            >
              <div class="stzh-geo-ref-data__filters">
                {this._availableTags.length > 0 && (
                  <div class="stzh-geo-ref-data__filters-toggle">
                    <stzh-chip
                      label={this.isViewportMedium ? "Filter" : ""}
                      variant="filter"
                      icon="filtering"
                      onStzhClick={this.handleFilterToggleClick}
                    ></stzh-chip>
                  </div>
                )}
                <div
                  class={{
                    "stzh-geo-ref-data__filters-content": true,
                    "stzh-geo-ref-data__filters-content--visible": !this.isViewportLarge && this.isFiltersContentOpen
                  }}
                >
                  <stzh-chipselect
                    label-hidden={true}
                    label="Tags"
                    data={this.enhancedTags}
                    onStzhChange={this.handleFilterChange}
                    multiple={true}
                    defaultValue={this.appliedFilters}
                    value={this.appliedFilters}
                  ></stzh-chipselect>
                  <div class="stzh-geo-ref-data__filters-apply-button">
                    <stzh-button size="small" fullwidth={true} onStzhClick={this.handleApplyFilterClick}>
                      Anwenden
                    </stzh-button>
                  </div>
                </div>
              </div>
              <div class="stzh-geo-ref-data__search">
                <stzh-input
                  name="stzh-datatable-search"
                  onStzhChange={this.handleSearchChange}
                  hideOptional={true}
                  label="Suche"
                  type="search"
                ></stzh-input>
              </div>
              <div class="stzh-geo-ref-data__results-toggle">
                <stzh-chip
                  onStzhClick={this.toggleViewMode}
                  label={this.viewmode === "map" ? "Liste" : "Karte"}
                  icon={this.viewmode === "map" ? "bulleted-list" : "map"}
                  variant="filter"
                ></stzh-chip>
              </div>
            </div>
            <div class="stzh-geo-ref-data__results-wrapper">
              <div class={`stzh-geo-ref-data__results-map ${this.viewmode === "map" ? "active" : "inactive"}`}>
                <stzh-olmap
                  class="stzh-geo-ref-data__ol-map"
                  centerMarker={this.centerMarker}
                  zoom={this.zoom}
                  zoomLocation={this.zoomLocation}
                  writeExtentToUrlEnabled={this.isOpen}
                  locationControl={this.locationControl}
                  pinchRotateThreshold={this.pinchRotateThreshold}
                  ref={el => (this.mapElement = el as HTMLStzhOlmapElement)}
                ></stzh-olmap>
                <div class="stzh-geo-ref-data__map-controls">
                  <stzh-checkbox
                    label="Treffer an Kartenausschnitt anpassen"
                    checked={this.showOnlyResultsForCurrentExtent}
                    hideOptional={true}
                    onStzhChange={this.handleExtentVisibilityChange}
                  ></stzh-checkbox>
                </div>
              </div>
              <div
                class={`stzh-geo-ref-data__results-list ${this.viewmode === "list" ? "active" : "inactive"}`}
                ref={el => (this.resultsListElement = el as HTMLDivElement)}
              >
                {this.finalResults && (
                  <Fragment>
                    <div class="stzh-geo-ref-data__list-search">
                      <stzh-input
                        name="stzh-datatable-search"
                        onStzhChange={this.handleSearchChange}
                        hideOptional={true}
                        label="Suche"
                        type="search"
                      ></stzh-input>
                    </div>
                    <stzh-text class="stzh-geo-ref-data__results-list-heading">
                      {this.finalResults.length} Treffer
                    </stzh-text>
                    <div class="stzh-geo-ref-data__teaser-list">
                      {this.finalResultsPaginatedList.map(item => (
                        <div
                          class="stzh-poi-teaser"
                          onMouseEnter={() => this.handlePoiTeaserMouseEnter(item)}
                          onMouseLeave={() => this.handlePoiTeaserMouseLeave(item)}
                        >
                          <a class="stzh-poi-teaser-link" href={this.baseUrlOverride + item.path}></a>
                          <div class="stzh-poi-teaser-content">
                            <stzh-heading curve="lead">{item.poiname}</stzh-heading>
                            {item.description && <stzh-text size="micro">{item.description}</stzh-text>}
                            {item.data &&
                              item.data.length > 0 &&
                              item.data.map(item => (
                                <div class="stzh-poi-teaser-list-item">
                                  <stzh-icon name={item.icon}></stzh-icon>
                                  <stzh-text size="micro">{item.text}</stzh-text>
                                </div>
                              ))}
                          </div>
                          {item.image && (
                            <div class="stzh-poi-teaser__image-wrapper">
                              <stzh-ratio
                                ratio={"2:1"}
                                ratio-large={"5:4"}
                                ratio-ultra={"1:1"}
                                portrait={this.isViewportLarge}
                                class="stzh-poi-teaser__ratio"
                              ></stzh-ratio>
                              <div class="stzh-poi-teaser__image-inner-wrapper">
                                <img src={this.baseUrlOverride + item.image} alt={"Foto von " + item.poiname} />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {this.canLoadMore && (
                      <div class="stzh-geo-ref-data__teaser-load-more">
                        <stzh-button onStzhClick={() => this.loadMore()}>Weitere laden</stzh-button>
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
